/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import BaseSummarizerQuery from './BaseSummarizerQuery';
import FilteredSummaryValueQuery from './FilteredSummaryValueQuery';
import SummaryCollector from './SummaryCollector';

/**
 * The CombinedSummaryValueQuery determines when and how to query for a combined summary value based on a set of rules.
 * A caller simply calls the function and a value is returned which is either null or the query result as values in a
 * summary collector (for the ones it calculated).
 * In general, the query works by producing an "OR'ed result" of the filter state of the widgets passed in in the constructor.
 * Currently, this query is only made for aggregation type countdistinct but in theorym, could be extended to other aggregation types.
 */
class CombinedSummaryValueQuery extends BaseSummarizerQuery {
	/**
	 * @Constructor
	 * @param {Object} widgetApis - An array of widgetApis to consider.
	 * @param {Object} dashboardApi - the dashboardApi
	 * @param {Object} logger - The logger
	 * @param {boolean} isSelectionStats - true if this is a selection summary.
	 * @param {Object} summaryCollector - (optional) For testing.
	 */
	constructor(widgetApis, dashboardApi, logger, isSelectionStats, summaryCollector = null) {
		super(widgetApis[0], dashboardApi, logger);
		this.widgetApis=widgetApis;

		this.summaryCollector = summaryCollector || new SummaryCollector(dashboardApi, this.widgetApi, isSelectionStats);

		this.summaryValueQueries = [];
		widgetApis.forEach((widgetApi) => {
			//When doing combined summaries, we don't want to use cached single values
			//Because they need to be "OR'ed" into the combined filter.
			this.summaryValueQueries.push(new FilteredSummaryValueQuery(widgetApi, dashboardApi, logger));
		});

		this.widgetFilters = [];
		this.isSelectionStats = isSelectionStats;
	}

	/**
	 * @returns true if some unprocessed item is common to both visualizations.
	 */
	hasUnprocessedItems() {
		let commonItems = null;
		this.summaryValueQueries.forEach((summaryValueQuery) => {
			const thisWidgetItems = summaryValueQuery.getUnprocessedItems();
			commonItems = commonItems ? commonItems.filter((checkItem) => thisWidgetItems.indexOf(checkItem) !== -1) : thisWidgetItems;
		});
		return commonItems.length > 0;
	}

	/**
	 * Build a query definition for the combined Summary value query if it applies.
	 * The items/widgets involved in the query must meet a number of conditions in order to build the query definition.
	 * If they do not meet the criteria, no query is needed and this funtion returns null.
	 * @returns queryDef - a queryDefinition JSON spec (or NULL if no query should be executed)
	 */
	build() {
		const queryDef = {
			dataItems: [],
			projections: []
		};

		//RULE: Need at least 2 widgets (query object) to need a combined value.
		if (this.summaryValueQueries.length < 2) {
			return null;
		}
		let widgetsWithSummaryItemsToCombine = 0;
		let commonItems = null;
		this.summaryValueQueries.forEach((summaryValueQuery) => {
			queryDef.dataItems=[];
			queryDef.projections=[];
			summaryValueQuery.build(this.summaryCollector, queryDef, this.isSelectionStats);
			//Keep tabs on the number of widgets whose summaries need to be combined (have countdistinct measures)
			widgetsWithSummaryItemsToCombine += (queryDef.dataItems.length > 0) ? 1 : 0;
			//Only widgets where both have the same countdistinct itemId need a combinedSummaryValue
			const thisWidgetItems = queryDef.dataItems.map((dataItem) => dataItem.aggregate + ':' + dataItem.itemId);
			commonItems = commonItems ? commonItems.filter((checkItem) => thisWidgetItems.indexOf(checkItem) !== -1) : thisWidgetItems;

			if (queryDef.filters && queryDef.filters.length > 0) {
				this.widgetFilters.push(queryDef.filters);
				queryDef.filters = null;
			}
		});

		//RULE: Need at least 2 queries with applicable items (count distinct measures) to need a combined value.
		if (widgetsWithSummaryItemsToCombine < 2) {
			return null;
		}

		//RULE: Need at least 2 queries with common items to need a combined value.
		if (commonItems.length === 0) {
			return null;
		}

		//Now that we've collected all of the filters, "OR" them together (or return [] if either has no filters.)
		queryDef.filters = this.combineFilters();

		return queryDef;
	}

	/**
	 * If necessary, execute a query for summary values combined across the set of visualizations
	 * specified in the constructor.
	 * @returns a promise with the query result....or null if no query needed to execute (see build rules)
	 */
	execute() {
		const queryDef = this.build();
		if (!queryDef) {
			return Promise.resolve(null);
		}

		//Query for any summaries that passed all the "need to query rules"
		return this._execute(this.summaryCollector, queryDef).then(() => {
			return this.summaryCollector;
		});
	}

	/**
	 * Process the value of an item returned from the query by adding it to the summaryCollector
	 * @param {String} dataItemUniqueId - the id of the dataItem.
	 * @param {String} aggregate - the aggregation type (eg countdistinct)
	 * @param {String} filteredSummaryUseValue - the actual value (to be added as the filteredSummaryUseValue)
	 * @param {Object} summaryCollector - the summaryCollector to add the value to.
	 */
	processValue(dataItemUniqueId, aggregate, filteredSummaryUseValue, summaryCollector) {
		this.updateSummaryEntry(summaryCollector, dataItemUniqueId, filteredSummaryUseValue, 'filteredSummaryValueUse', 'filteredSummaryValue');
	}

	/**
	 * Produce a combined summary result by OR'ing the filters on the two visualizations.
	 * ie: if Widget1 is filtered on Canada and Mexico and widget2 is filtered on USA,
	 * the combined count would be the union count for Canada, USA and Mexico
	 * @returns a filterJSON spec that is the union of A and B.  If A or BV has no filters, it returns [].
	 */
	combineFilters() {
		if (this.widgetFilters.length < 2) {
			//If either of the widgets are unfiltered, OR is like ALL so no filtering is needed.
			return [];
		}

		const combinedFilters = {
			operator: 'or',
			values: []
		};

		this.widgetFilters.forEach((filters) => {
			const widgetFilters = { operator: 'and', values: [] };
			filters.forEach((widgetFilter) => {
				//TODO: guard against post-aggregate filters.
				widgetFilters.values.push(widgetFilter);
			});
			combinedFilters.values.push(widgetFilters);
		});
		return [combinedFilters];
	}
}

export default CombinedSummaryValueQuery;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-summarizer/src/js/CombinedSummaryValueQuery.js